import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Radio, Space, Switch, Typography } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CODIFICATION_QUESTION_TYPE } from '../../../constant/codification';
import { TH5PQuizOption } from '../../../models/courses/h5p';
import { createDefaultOption } from '../utils';

type QuestionType = typeof CODIFICATION_QUESTION_TYPE[keyof typeof CODIFICATION_QUESTION_TYPE];

interface H5PAnswerEditorListProps {
  questionType: QuestionType;
  answers: TH5PQuizOption[];
  onChange: (answers: TH5PQuizOption[]) => void;
}

const ensureSingleAnswer = (answers: TH5PQuizOption[]): TH5PQuizOption[] => {
  if (answers.length === 0) {
    return [
      {
        id: '0',
        label: '',
        isCorrect: true,
      },
    ];
  }
  return answers.slice(0, 1).map((answer) => ({
    ...answer,
    isCorrect: true,
  }));
};

const H5PAnswerEditorList: React.FC<H5PAnswerEditorListProps> = ({
  questionType,
  answers,
  onChange,
}) => {
  const { t } = useTranslation();

  if (
    questionType === CODIFICATION_QUESTION_TYPE.TEXT_INPUT ||
    questionType === CODIFICATION_QUESTION_TYPE.NUMERIC_INPUT
  ) {
    const singleAnswer = ensureSingleAnswer(answers)[0];

    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text strong>{t('h5p.answerEditor.expectedAnswer')}</Typography.Text>
        <Input
          value={singleAnswer.label}
          placeholder={
            questionType === CODIFICATION_QUESTION_TYPE.NUMERIC_INPUT
              ? t('h5p.answerEditor.numericPlaceholder')
              : t('h5p.answerEditor.textPlaceholder')
          }
          onChange={(event) =>
            onChange([
              {
                ...singleAnswer,
                label: event.target.value,
                isCorrect: true,
              },
            ])
          }
        />
      </Space>
    );
  }
  if (questionType === CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS) {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text strong>{t('h5p.answerEditor.fillTitle')}</Typography.Text>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('h5p.answerEditor.fillDescription')}
        </Typography.Paragraph>
        <Space direction="vertical" style={{ width: '100%' }}>
          {answers.map((answer) => (
            <Space key={answer.id} style={{ width: '100%' }} align="center">
              <Input
                value={answer.label}
                onChange={(event) =>
                  onChange(
                    answers.map((current) =>
                      current.id === answer.id
                        ? { ...current, label: event.target.value, isCorrect: true }
                        : { ...current, isCorrect: true },
                    ),
                  )
                }
                placeholder={t('h5p.answerEditor.fillPlaceholder')}
              />
              <Button
                icon={<DeleteOutlined />}
                danger
                type="text"
                disabled={answers.length <= 1}
                onClick={() => onChange(answers.filter((current) => current.id !== answer.id))}
              />
            </Space>
          ))}
        </Space>
        <Button
          icon={<PlusOutlined />}
          onClick={() =>
            onChange([
              ...answers.map((answer) => ({ ...answer, isCorrect: true })),
              { ...createDefaultOption(), isCorrect: true },
            ])
          }
        >
          {t('h5p.answerEditor.addBlank')}
        </Button>
      </Space>
    );
  }

  if (questionType === CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP) {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text strong>{t('h5p.answerEditor.dragTitle')}</Typography.Text>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('h5p.answerEditor.dragDescription')}
        </Typography.Paragraph>
        <Space direction="vertical" style={{ width: '100%' }}>
          {answers.map((answer) => (
            <Space key={answer.id} style={{ width: '100%' }} align="center">
              <Switch
                checked={answer.isCorrect}
                onChange={(checked) =>
                  onChange(
                    answers.map((current) =>
                      current.id === answer.id
                        ? { ...current, isCorrect: checked }
                        : current,
                    ),
                  )
                }
                checkedChildren={t('h5p.answerEditor.dragTarget')}
                unCheckedChildren={t('h5p.answerEditor.dragOption')}
              />
              <Input
                value={answer.label}
                onChange={(event) =>
                  onChange(
                    answers.map((current) =>
                      current.id === answer.id
                        ? { ...current, label: event.target.value }
                        : current,
                    ),
                  )
                }
                placeholder={t('h5p.answerEditor.dragPlaceholder')}
              />
              <Button
                icon={<DeleteOutlined />}
                danger
                type="text"
                onClick={() => onChange(answers.filter((current) => current.id !== answer.id))}
              />
            </Space>
          ))}
        </Space>
        <Button icon={<PlusOutlined />} onClick={() => onChange([...answers, createDefaultOption()])}>
          {t('h5p.answerEditor.addAnswer')}
        </Button>
      </Space>
    );
  }

  if (questionType === CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE) {
    const selectedId = answers.find((answer) => answer.isCorrect)?.id;

    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Text strong>{t('h5p.answerEditor.singleChoiceTitle')}</Typography.Text>
        <Radio.Group
          value={selectedId}
          style={{ width: '100%' }}
          onChange={(event) => {
            onChange(
              answers.map((answer) => ({
                ...answer,
                isCorrect: answer.id === event.target.value,
              })),
            );
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {answers.map((answer) => (
              <Space key={answer.id} style={{ width: '100%' }} align="center">
                <Radio value={answer.id} />
                <Input
                  value={answer.label}
                  onChange={(event) =>
                    onChange(
                      answers.map((current) =>
                        current.id === answer.id
                          ? { ...current, label: event.target.value }
                          : current,
                      ),
                    )
                  }
                  placeholder={t('h5p.answerEditor.answerPlaceholder')}
                />
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  type="text"
                  onClick={() =>
                    onChange(answers.filter((current) => current.id !== answer.id))
                  }
                />
              </Space>
            ))}
          </Space>
        </Radio.Group>
        <Button
          icon={<PlusOutlined />}
          onClick={() => onChange([...answers, createDefaultOption()])}
        >
          {t('h5p.answerEditor.addAnswer')}
        </Button>
      </Space>
    );
  }

  // Multi choice handling
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Text strong>{t('h5p.answerEditor.multiChoiceTitle')}</Typography.Text>
      <Space direction="vertical" style={{ width: '100%' }}>
        {answers.map((answer) => (
          <Space key={answer.id} style={{ width: '100%' }} align="center">
            <Checkbox
              checked={answer.isCorrect}
              onChange={(event) =>
                onChange(
                  answers.map((current) =>
                    current.id === answer.id
                      ? { ...current, isCorrect: event.target.checked }
                      : current,
                  ),
                )
              }
            />
            <Input
              value={answer.label}
              onChange={(event) =>
                onChange(
                  answers.map((current) =>
                    current.id === answer.id
                      ? { ...current, label: event.target.value }
                      : current,
                  ),
                )
              }
              placeholder={t('h5p.answerEditor.answerPlaceholder')}
            />
            <Button
              icon={<DeleteOutlined />}
              danger
              type="text"
              onClick={() =>
                onChange(answers.filter((current) => current.id !== answer.id))
              }
            />
          </Space>
        ))}
      </Space>
      <Button icon={<PlusOutlined />} onClick={() => onChange([...answers, createDefaultOption()])}>
        {t('h5p.answerEditor.addAnswer')}
      </Button>
    </Space>
  );
};

export default H5PAnswerEditorList;